import { ReactNode, useMemo } from "react";
import ResizableBox from "./ResizableBox";
import { AxisOptions, AxisOptionsBase, Chart } from "react-charts";
import useChartConfig from "./chartConfig";
import { SwitchAppInlineLoadingIcon } from "../SwitchAppInlineLoadingIcon";

import warningIcon from "../../assets/icons/warning.svg";
import { IMultiSeriesChartData } from ".";

export interface ILineChartProps {
  isLoading: boolean;
  isError: boolean;
  data: IMultiSeriesChartData[];
  total: ReactNode;
  title: string;
  description?: string;
  chartType?: AxisOptionsBase["elementType"];
  stacked?: boolean;
}

export function LineChart(props: ILineChartProps) {
  const {
    isLoading,
    isError,
    data,
    chartType = "line",
    description,
    stacked,
    title,
    total,
  } = props;

  const { ...restConfig } = useChartConfig({
    series: 3,
    dataType: "time", // "timeLocal"
  });

  const primaryAxis = useMemo<AxisOptions<typeof data[number]["data"][number]>>(
    () => ({
      getValue: (datum) => datum.x,
      // scaleType: "time"
    }),
    [chartType]
  );

  const secondaryAxes = useMemo<
    AxisOptions<typeof data[number]["data"][number]>[]
  >(
    () => [
      {
        getValue: (datum) => datum.y,
        elementType: chartType,
        stacked,
        // scaleType: "time"
      },
    ],
    [chartType]
  );

  return (
    <div className="pointers flex-box flex-container col">
      <div className="flex-box">
        <h6>{title}</h6>
        <small>{description}</small>
        <h5 className="chart-total">{total}</h5>
      </div>

      <ResizableBox
        resizable={false}
        className="animated-chart--view flex-container align-items--stretch"
      >
        {isError ? (
          <div className="flex-box flex-container justify-content--center align-items--center bordered b-r-5">
            <div className="mr-5">
              <img
                src={warningIcon}
                alt="error"
                className="payment-completed--icon"
              />
            </div>
            <span className="switchapp-small">error loading data</span>
          </div>
        ) : isLoading ? (
          <div className="flex-box flex-container justify-content--center align-items--center bordered b-r-5">
            <SwitchAppInlineLoadingIcon description="loading chart data..." />
          </div>
        ) : (
          <Chart
            options={{
              data,
              primaryAxis,
              secondaryAxes,
              padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              },
              ...restConfig,
            }}
          />
        )}
      </ResizableBox>
    </div>
  );
}
